import type { AudioType } from "@expert/shared-types";

import { sendErrorMetric, sendEventMetric } from "@expert/cloudwatch-metrics";
import { Accordion, ActionIcon, Box, Flex, Group, Modal, Progress, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconMessage, IconPlayerPlayFilled, IconQuote } from "@tabler/icons-react";
import { type JSX, useEffect, useEffectEvent, useRef, useState } from "react";
import { useTimer } from "react-timer-hook";

import {
    DisclosurePlayError,
    type DisclosurePlayFailureReason,
} from "../../../../../sales-context/disclosurePlayError";
import { useSalesContext } from "../../../../../sales-context/SalesContext";
import { useLanguageStore } from "../../language-selector/useLanguageStore";
import { getDisclosureLengthSeconds, getDisclosureModalText } from "../utils/disclosureContent";
import { shouldUsePreRecordedDisclosures } from "../utils/shouldUsePreRecordedDisclosures";
import styles from "./RecordedLegalDisclosuresSection.module.css";

const PENDING_TIMEOUT_MS = 5000;

// Tolerance for "natural completion" when the SDK signals playback ended. The "ended" event
// fires roughly at the end of the recording but can race with our local timer's onExpire and
// arrive a fraction of a second early. Treat any "ended" within this window of full length as
// a successful play.
const NATURAL_END_TOLERANCE_MS = 2000;

const SCRIPT_TEXT_BY_LANGUAGE: Record<AudioType, string> = {
    // English wording is the corrected script from #6844 (merged to main).
    PreRecordedDisclosures:
        "I'm going to play you a short recording with important plan disclosures. Let's stay on the line together and then I'll finish your enrollment.",
    // TODO: Spanish still reflects the pre-#6844 English script — re-translate to match the corrected
    // English wording above and get it re-approved before Spanish disclosures are enabled.
    PreRecordedDisclosuresSpanish:
        "Voy a reproducirle esta breve grabación de las divulgaciones y luego lo inscribiremos.",
};

type Props = {
    productSku: string;
    disclosureEndTime: Date | null;
    onSetDisclosureEndTime: (d: Date | null) => void;
    /** Called with the AudioType that actually played (captured in-flight, so a mid-play language
     * switch credits completion to the audio the customer heard — not the current selection). */
    onDisclosureCompleted: (audioType: AudioType) => void;
    /** Called when the disclosure audio can't be played (conference failed) so the flow degrades to Continue. */
    onDisclosureAudioFailed?: () => void;
};

export function RecordedLegalDisclosuresSection({
    productSku,
    disclosureEndTime,
    onSetDisclosureEndTime,
    onDisclosureCompleted,
    onDisclosureAudioFailed = () => {},
}: Props): JSX.Element | null {
    const {
        features,
        taskType,
        onPlayDisclosure,
        disclosurePlayback,
        analytics,
        logger,
        partner,
        disclosureAudioAvailability,
        isCustomerOnHold,
        isSimulatedVoiceTask,
    } = useSalesContext();
    const { audioType, label: selectedLanguage } = useLanguageStore();

    const usePreRecordedDisclosure = shouldUsePreRecordedDisclosures({
        productSku,
        features,
        taskType,
        isSimulatedVoiceTask,
    });

    const disclosureLengthSeconds = getDisclosureLengthSeconds(productSku, audioType);
    const disclosureModalText = getDisclosureModalText(productSku, audioType);
    const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
    const [isPending, setIsPending] = useState(false);
    // Set when a play attempt fails so this section stops rendering and the step degrades to Continue.
    const [audioFailed, setAudioFailed] = useState(false);
    // Set to the retryable failure reason when a play is pre-empted by a transient condition — the
    // customer leg isn't connected yet ("contact_not_ready", e.g. Play pressed during a callback
    // before the customer answered) or a disclosure is already playing ("in_flight"). Unlike
    // audioFailed these don't degrade: a reason-specific hint is shown and the button stays available
    // so the agent can retry, rather than permanently losing a disclosure that can still play.
    const [retryReason, setRetryReason] = useState<DisclosurePlayFailureReason | null>(null);
    const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Remember when playback started so we can tell natural-end (~47s elapsed) apart from
    // early-hangup on the SDK's `disclosure_playback_ended` signal — both look identical
    // (disclosurePlayback transitions to null) but only the former should mark completion.
    const startedAtRef = useRef<number | null>(null);
    // Length (seconds) of the audio that actually started playing, captured at click time. The timer
    // must stay anchored to the audio in flight even if the expert switches language mid-playback —
    // re-anchoring to the newly-selected length would unlock Enroll early (or stall completion).
    const playedLengthSecondsRef = useRef<number | null>(null);
    // AudioType (language) of the audio that actually started playing, captured at click time.
    // Completion is credited to this, not the current selection, so switching language mid-play
    // can't unlock Enroll for a language the customer never heard.
    const playedAudioTypeRef = useRef<AudioType | null>(null);
    // Emit the "disclosure completed" signal exactly once per play — both completion paths (the local
    // timer's onExpire and the SDK natural-end branch) can fire, and this ref keeps it single-count.
    // Reset at the start of each play in handlePlay.
    const completionEmittedRef = useRef(false);
    // Dedup the "blocked by hold" near-miss to once per hold episode (re-armed when off hold).
    const onHoldReportedRef = useRef(false);

    // Fallback to 0s when the SKU isn't recognized so useTimer stays valid; the early-return
    // gate below will render null before any of the timer values reach the UI.
    const lengthSeconds = disclosureLengthSeconds ?? 0;
    const expiryTimestamp = disclosureEndTime ?? new Date(Date.now() + lengthSeconds * 1000);

    // Positive observability signal: the disclosure actually played to completion. Emitted once per
    // play (guarded by completionEmittedRef) so health can be measured directly rather than inferred
    // from the absence of error metrics. Credited to the in-flight language.
    const emitDisclosureCompleted = (playedAudioType: AudioType) => {
        if (completionEmittedRef.current) return;
        completionEmittedRef.current = true;
        logger.info({ partner, audioType: playedAudioType, productSku }, "disclosure_completed");
        void sendEventMetric("DisclosureCompleted", { partner, audioType: playedAudioType, productSku });
        void analytics.dispatcher.dispatchBusinessEvent("DisclosureCompleted", {
            partner,
            audioType: playedAudioType,
            productSku,
        });
    };

    const { totalSeconds, isRunning, restart, pause } = useTimer({
        expiryTimestamp,
        autoStart: !!disclosureEndTime,
        onExpire: () => {
            onSetDisclosureEndTime(null);
            const playedAudioType = playedAudioTypeRef.current ?? audioType;
            emitDisclosureCompleted(playedAudioType);
            onDisclosureCompleted(playedAudioType);
        },
    });

    // SDK confirms the disclosure connection has actually joined the conference — anchor the
    // timer to that moment instead of the click time, so latency between click and audio-start
    // doesn't cause the progress bar to finish early.
    useEffect(() => {
        if (disclosurePlayback?.state === "playing") {
            if (pendingTimeoutRef.current) {
                clearTimeout(pendingTimeoutRef.current);
                pendingTimeoutRef.current = null;
            }
            setIsPending(false);
            startedAtRef.current = disclosurePlayback.startedAt;
            // Anchor to the length of the audio that actually started (captured at click), NOT the
            // currently-selected length — otherwise a mid-play language switch re-anchors the timer.
            const playedLengthSeconds = playedLengthSecondsRef.current ?? lengthSeconds;
            const newExpiry = new Date(disclosurePlayback.startedAt + playedLengthSeconds * 1000);
            onSetDisclosureEndTime(newExpiry);
            restart(newExpiry, true);
        } else if (disclosurePlayback === null) {
            // If enough time elapsed since we started, the SDK's "ended" signal means the
            // recording finished naturally (the most common case — the Quick Connect call
            // drops on its own once the audio finishes). Mark completion before pausing,
            // because pause() prevents the local timer's onExpire from ever firing.
            const startedAt = startedAtRef.current;
            const playedLengthSeconds = playedLengthSecondsRef.current ?? lengthSeconds;
            const playedAudioType = playedAudioTypeRef.current ?? audioType;
            if (startedAt !== null && Date.now() - startedAt >= playedLengthSeconds * 1000 - NATURAL_END_TOLERANCE_MS) {
                emitDisclosureCompleted(playedAudioType);
                onDisclosureCompleted(playedAudioType);
            }
            startedAtRef.current = null;
            playedLengthSecondsRef.current = null;
            playedAudioTypeRef.current = null;
            // Pause so isRunning flips to false immediately. Without this, when the SDK signals
            // playback ended (e.g., customer hung up early), the timer would keep counting down
            // its original 47s and keep the button disabled.
            pause();
            onSetDisclosureEndTime(null);
        }
        // `lengthSeconds` intentionally omitted: the running timer is anchored to the captured
        // in-flight length (playedLengthSecondsRef), so selection changes must not re-anchor it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disclosurePlayback, onSetDisclosureEndTime, onDisclosureCompleted, restart, pause]);

    useEffect(() => {
        return () => {
            if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
        };
    }, []);

    // A disclosure still needs playing (section visible, not currently playing/pending, not completed).
    // Mirrors the early-return gate below (which can't be read before it runs, since hooks must precede it).
    const disclosureAwaitingPlay =
        usePreRecordedDisclosure &&
        disclosureAudioAvailability[audioType] &&
        !!onPlayDisclosure &&
        !!disclosureLengthSeconds &&
        !!disclosureModalText &&
        !audioFailed &&
        !isRunning &&
        !isPending &&
        disclosurePlayback?.state !== "playing" &&
        !completionEmittedRef.current;

    // Compliance near-miss: the customer was put on hold while a required disclosure still had to be
    // played, so it couldn't start. Emitted once per hold episode (re-armed when off hold).
    const reportBlockedByHold = useEffectEvent(() => {
        logger.info({ partner, audioType, productSku }, "disclosure_play_blocked_by_hold");
        void sendEventMetric("DisclosurePlayBlockedByHold", { partner, audioType, productSku });
        void analytics.dispatcher.dispatchBusinessEvent("DisclosurePlayBlockedByHold", {
            partner,
            audioType,
            productSku,
        });
    });
    useEffect(() => {
        if (isCustomerOnHold && disclosureAwaitingPlay && !onHoldReportedRef.current) {
            onHoldReportedRef.current = true;
            reportBlockedByHold();
        } else if (!isCustomerOnHold) {
            onHoldReportedRef.current = false;
        }
    }, [isCustomerOnHold, disclosureAwaitingPlay]);

    if (
        !usePreRecordedDisclosure ||
        !disclosureAudioAvailability[audioType] ||
        !onPlayDisclosure ||
        !disclosureLengthSeconds ||
        !disclosureModalText ||
        audioFailed
    ) {
        return null;
    }

    const handlePlay = async () => {
        // Compliance guard (defensive; the button is also disabled): never start a disclosure while
        // the customer is on hold — they wouldn't hear it.
        if (isCustomerOnHold) return;
        // Clear any prior retryable hint at the start of a fresh attempt.
        setRetryReason(null);
        void analytics.dispatcher.dispatchBusinessEvent("PlayLegalDisclosure_Clicked", {
            selectedLanguage,
            partner,
        });
        // Capture the length of the audio being played NOW so the timer stays anchored to it even if
        // the expert switches language before it finishes (see the playback effect above).
        playedLengthSecondsRef.current = disclosureLengthSeconds ?? 0;
        playedAudioTypeRef.current = audioType;
        completionEmittedRef.current = false; // new play — re-arm the once-per-play completion signal
        setIsPending(true);
        // Safety: if the SDK never confirms playback (Quick Connect misconfigured, Connect drops the
        // request silently), degrade after a timeout. addConnection may have "succeeded" yet no audio
        // plays and no `disclosure_playback_started` arrives — without this the Enroll gate never
        // releases and the agent is stuck. On confirmed playback the effect above clears this timeout,
        // so a fire here means playback was never confirmed → degrade to the manual Continue flow.
        pendingTimeoutRef.current = setTimeout(() => {
            // Reaching here means playback was never confirmed: the playback effect and the catch
            // branch both clear this timeout, so if it fires, addConnection neither confirmed nor
            // threw. Degrade to the manual Continue flow rather than leaving the Enroll gate stuck.
            pendingTimeoutRef.current = null;
            setIsPending(false);
            logger.warn("Disclosure playback did not start within timeout; degrading to Continue");
            void sendErrorMetric("LegalDisclosurePlayError", {
                partner,
                audioType: playedAudioTypeRef.current ?? audioType,
                reason: "timeout",
            });
            setAudioFailed(true);
            onDisclosureAudioFailed();
        }, PENDING_TIMEOUT_MS);

        try {
            await onPlayDisclosure(audioType);
        } catch (error) {
            // If the safety timeout already fired it has already degraded to Continue and reported
            // the metric — don't double-fire. Only handle here if this catch wins the race
            // (pendingTimeoutRef still set). Guards against a rejection arriving after the timeout.
            if (!pendingTimeoutRef.current) {
                // Degradation already happened via the timeout, so keep this at info (not error) — but
                // still log it so a late/systematic SDK rejection that loses the race isn't invisible.
                logger.info({ error }, "Disclosure rejection arrived after timeout; already degraded");
                return;
            }
            clearTimeout(pendingTimeoutRef.current);
            pendingTimeoutRef.current = null;
            setIsPending(false);
            // The host maps SDK-specific failures to a typed DisclosurePlayError.reason at the
            // onPlayDisclosure boundary, so categorize by that typed reason (no SDK coupling).
            // Anything else is treated as a genuine conference failure.
            const reason: DisclosurePlayFailureReason =
                error instanceof DisclosurePlayError ? error.reason : "conference_error";
            const isTransient = reason === "contact_not_ready" || reason === "in_flight";
            // contact_not_ready / in_flight are expected, transient, and retryable — log at info so
            // they don't trip error-rate alerting. Only genuine conference failures log at error.
            if (isTransient) {
                logger.info({ error, reason }, "Disclosure play pre-empted (transient); retryable");
            } else {
                logger.error({ error, reason }, "Failed to play legal disclosure");
            }
            void sendErrorMetric("LegalDisclosurePlayError", {
                partner,
                audioType: playedAudioTypeRef.current ?? audioType,
                reason,
            });
            if (isTransient) {
                // The customer leg isn't connected yet (Play pressed during a callback before the
                // customer answered) or a disclosure is already playing. Surface a reason-specific
                // hint and leave the button available so the agent can retry — do NOT degrade, since
                // the disclosure can still play. (No reliable reactive "connected" signal exists for
                // outbound legs to disable the button pre-emptively, so this retryable path is the
                // safe guard.)
                setRetryReason(reason);
                return;
            }
            // Genuine conference failure — degrade to the standard Continue flow so the agent
            // isn't stuck behind audio that can't play.
            setAudioFailed(true);
            onDisclosureAudioFailed();
        }
    };

    const elapsedSeconds = isRunning || disclosureEndTime ? disclosureLengthSeconds - totalSeconds : 0;
    const progressValue = (elapsedSeconds / disclosureLengthSeconds) * 100;

    return (
        <>
            <Accordion.Item value="recorded-legal-disclosures">
                <Accordion.Control styles={{ control: { opacity: 1 } }}>
                    <Group justify="space-between" pr={"sm"}>
                        <Text size="xs" c="dark.1" fw="var(--mantine-font-weight-heavy)">
                            Play Disclosures (required)
                        </Text>
                    </Group>
                </Accordion.Control>
                <Accordion.Panel>
                    <Stack gap="md" p="sm">
                        <Text fw={700} size="md">
                            <Text span c="teal.4" inherit>
                                Play
                            </Text>{" "}
                            the disclosures
                        </Text>

                        <Group align="flex-start" gap="xs" wrap="nowrap">
                            <IconQuote
                                size={16}
                                color="var(--mantine-color-teal-4)"
                                className={styles.quoteIconFlipped}
                            />
                            <Text size="sm" c="dark.0">
                                {SCRIPT_TEXT_BY_LANGUAGE[audioType]}
                            </Text>
                        </Group>

                        <Stack gap={4}>
                            <Group justify="space-between">
                                <Text size="xs" c="dark.1" ff="monospace">
                                    {formatTime(elapsedSeconds)}
                                </Text>
                                <Text size="xs" c="dark.1" ff="monospace">
                                    {formatTime(disclosureLengthSeconds)}
                                </Text>
                            </Group>
                            <Progress
                                value={progressValue}
                                color="teal.4"
                                size="sm"
                                classNames={{ section: styles.progressBarSection }}
                            />
                        </Stack>

                        <Box pos="relative">
                            <Flex justify="center">
                                <ActionIcon
                                    size={48}
                                    radius="xl"
                                    color="violet.6"
                                    onClick={handlePlay}
                                    disabled={isPending || isRunning || isCustomerOnHold}
                                    aria-label="Play disclosure"
                                >
                                    <IconPlayerPlayFilled size={20} />
                                </ActionIcon>
                            </Flex>
                            {isCustomerOnHold && (
                                <Text size="xs" c="dark.1" ta="center" mt="xs">
                                    Take the customer off hold to play the disclosures.
                                </Text>
                            )}
                            {retryReason && !isCustomerOnHold && (
                                <Text size="xs" c="dark.1" ta="center" mt="xs">
                                    {retryReason === "in_flight"
                                        ? "Another disclosure is already playing. Wait for it to finish, then try again."
                                        : "The call isn't connected yet. Press Play once the customer is on the line."}
                                </Text>
                            )}
                            <ActionIcon
                                pos="absolute"
                                right={0}
                                top="50%"
                                style={{ transform: "translateY(-50%)" }}
                                variant="subtle"
                                color="gray"
                                onClick={openModal}
                                aria-label="View disclosure text"
                            >
                                <IconMessage size={20} />
                            </ActionIcon>
                        </Box>
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>

            <Modal opened={modalOpened} onClose={closeModal} title="Disclosure text" size="lg" centered>
                <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                    {disclosureModalText}
                </Text>
            </Modal>
        </>
    );
}

function formatTime(seconds: number): string {
    const safe = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(safe / 60);
    const secs = safe % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}
